/**
 * نمودارِ سوم: **خطِ لوله از دیدِ مرحله‌ها**.
 *
 * ═══ چرا سومی، در حالی که دو تا داریم ═══
 *
 * دو نمودارِ بالای صفحه هر کدام یک **واحد** را دنبال می‌کنند و در این کار
 * خوب‌اند: «سرنوشتِ درخواست‌ها» یک آدرس را دنبال می‌کند و «جریانِ خطِ لوله»
 * یک رویدادِ نامزد را. ولی هیچ‌کدام نمی‌گویند **خودِ مرحله‌ها چطور کار
 * می‌کنند**. سنکی جریانِ آیتم را می‌کشد، نه سلامتِ ماشینی که آیتم از تویش
 * رد می‌شود؛ و جدولِ «فازها» سلامت را می‌گوید ولی از جریانِ آیتم‌ها جداست و
 * آدم باید در ذهنش دو چیز را کنارِ هم بگذارد.
 *
 * این‌جا همان دو تا کنارِ هم‌اند: برای هر مرحله، هم شمارشِ آیتم‌ها (از قیف)
 * و هم کارنامه‌ی اجراها (از جدولِ فازها)، در یک ردیف.
 *
 * ═══ چرا سنکی نیست ═══
 *
 * چون سوالش فرق دارد. سنکی برای «این عدد از کجا به کجا رفت» است و ده ستون
 * کنارِ هم را نمی‌شود خواند. این‌جا سوال «در هر مرحله چه گذشت» است، و
 * جوابش یک فهرستِ عمودی است که از بالا به پایین خوانده می‌شود — با یک
 * نوارِ رنگی زیرِ هر مرحله که تقسیمِ ورودی‌اش را نشان می‌دهد.
 *
 * ضمناً HTML است و نه SVG، و این عمدی است: تمامِ نکته‌ی این نمودار توضیح
 * دادن است و هر تکه‌اش باید با نگه داشتنِ ماوس خودش را توضیح بدهد. در SVG
 * این کار یک کارتِ شناورِ دست‌ساز می‌خواهد (مثلِ `SankeyChart`)؛ در HTML
 * همان `title`ِ خودِ مرورگر است که بیرونِ جریانِ صفحه رندر می‌شود و هیچ‌جا
 * بریده نمی‌شود — همان دلیلی که سرِ `ui/Hint.tsx` نوشته شده.
 *
 * ⚠ **همه‌ی حساب‌ها در `core/lib/stageReport.ts` است و این فایل فقط شکل.**
 * همان تقسیمِ کاری که `SankeyChart`/`sankey.ts` دارند و به همان دلیل: عدد
 * را باید با `npm test` آزمود، شکل را نه.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import {
  ERROR_FAMILY, NOTSENT_WHY, fateMeta,
  type Fate, type FateKind, type Panel, type StageReport, type StageRow,
} from '@/core/lib/stageReport';
import { Two, fmtNum as fmt } from '@/ui/Num';
import { Hint } from '@/ui/Hint';

/** پیامِ واقعیِ خطا، نه `[object Object]` — همان درسِ سرِ `CrawlerTab`. */
const errText = (e: unknown): string => {
  if (typeof e === 'string') return e;
  if (e && typeof e === 'object') {
    const o = e as { message?: unknown; details?: unknown; hint?: unknown };
    return [o.message, o.details, o.hint].filter((x) => typeof x === 'string').join(' · ')
      || JSON.stringify(e);
  }
  return String(e);
};

/**
 * رنگِ هر جنسِ سرنوشت، و نامش به فارسی.
 *
 * ⚠ «ریزش» و «معطل» دو رنگِ جدا دارند و یکی کردنشان کلِ نمودار را بی‌معنی
 * می‌کند: «رد شد» تمام شده و کاری نمی‌شود کرد، ولی «در صفِ بازبینی» منتظرِ
 * یک آدم است. اولی خبر است، دومی کار.
 */
const KIND: Record<FateKind, { label: string; help: string }> = {
  pass: {
    label: 'رفت جلو',
    help: 'از این مرحله سالم رد شد و ورودیِ مرحله‌ی بعد است.',
  },
  drop: {
    label: 'ریخت',
    help: 'همین‌جا کار تمام شد. این آیتم هیچ‌وقت به اپ نمی‌رسد.',
  },
  hold: {
    label: 'معطل',
    help: 'نه رفت نه ریخت — یا نوبتش نرسیده یا منتظرِ تصمیمِ یک آدم است. '
      + 'با «ریخت» یکی نیست: این یکی هنوز قابلِ نجات است.',
  },
  info: {
    label: 'دانستنی',
    help: 'نه ریزش است نه پیشرفت؛ فقط چیزی که باید دانسته شود تا عددهای '
      + 'دیگر درست خوانده شوند.',
  },
};

/** ثانیه، با «—» برای وقتی که نچرخیده. صفرِ ثانیه با «نچرخید» یکی نیست. */
const secs = (n: number | null) => (n != null ? `${n}s` : '—');

const ago = (iso: string | null): string => {
  if (!iso) return 'هرگز';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins} دقیقه پیش`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} ساعت پیش`;
  return `${Math.round(hours / 24)} روز پیش`;
};

/** متنِ کاملِ کارتِ شناورِ یک سرنوشت — توضیح، مثال، و عددها. */
const fateHelp = (f: Fate, unit: string): string => [
  `${KIND[f.kind].label} — ${KIND[f.kind].help}`,
  f.why.replace(/\*\*/g, ''),
  f.like ? `مثال: ${f.like}` : '',
  f.share != null
    ? `${fmt(f.v.d10)} تا (${f.share}٪ از ورودیِ این مرحله) در ۱۰ روز، `
      + `${fmt(f.v.run)} تا در آخرین اجرا. واحد: ${unit}.`
    : `${fmt(f.v.d10)} تا در ۱۰ روز، ${fmt(f.v.run)} تا در آخرین اجرا.`,
].filter(Boolean).join('\n\n');

/**
 * نوارِ تقسیمِ ورودی.
 *
 * ⚠ درصدها از **ورودیِ همان مرحله** حساب می‌شوند و نه از کلِ خطِ لوله، پس
 * نوار همیشه پر است. اگر از کل حساب می‌شد، نوارِ مرحله‌های پایین‌دست چند
 * پیکسل می‌شد و دقیقاً جایی که باید دیده شود دیده نمی‌شد.
 */
function FateBar({ fates, total, unit }: { fates: Fate[]; total: number; unit: string }) {
  const seg = fates.filter((f) => !f.aside && f.v.d10 > 0);
  if (total <= 0 || !seg.length) {
    return <div className="pbar empty" title="در این پنجره هیچ آیتمی وارد این مرحله نشده." />;
  }
  return (
    <div className="pbar">
      {seg.map((f) => (
        <div key={f.id} className={`pseg ${f.kind}`}
          style={{ width: `${(f.v.d10 / total) * 100}%` }}
          title={`${f.label}: ${fateHelp(f, unit)}`} />
      ))}
    </div>
  );
}

/** برچسبِ فارسیِ سطل‌های نمودارِ درخواست‌ها، برای جدولِ تکرار. */
const BUCKET: Record<string, string> = {
  ok: 'گرفته شد',
  unchanged: 'عوض نشده بود',
  error: 'خطا',
  skipped: 'کشِ لینکِ مرده',
  robots: 'robots.txt',
  tier: 'حکمِ دسترسی',
  disabled: 'منبعِ خاموش',
  blocked_other: 'مسدودِ دیگر',
};

/**
 * جدولِ «چرا» زیرِ یک مرحله — **بسته، تا وقتی بازش کنی**.
 *
 * ⚠ سه شکل دارد و یکی کردنشان وسوسه‌انگیز است ولی غلط: ستون‌هایشان یکی
 * نیست. جدولِ تکرار چهار عدد دارد، جدولِ دلیل یک عدد و یک درصد، و جدولِ
 * نمونه اصلاً عدد ندارد — لینک دارد.
 *
 * ═══ چرا پیش‌فرض بسته است ═══
 *
 * نمودار سیزده مرحله دارد و هر مرحله می‌تواند جدول داشته باشد. باز بودنِ
 * همه یعنی صفحه‌ای که باید از بالا به پایین **خوانده** شود، به یک انبارِ
 * جدول تبدیل می‌شود و همان خطِ اصلی — «در هر مرحله چه گذشت» — گم می‌شود.
 *
 * ⚠ ولی شمارِ ردیف‌ها **روی خودِ دکمه** می‌آید و نه داخلِ جدول. چیزی که
 * بسته است و نگوید چقدر تویش هست، عملاً وجود ندارد: آدم بازش نمی‌کند چون
 * نمی‌داند ارزشش را دارد یا نه.
 *
 * دکمه‌ی واقعی است و نه یک `div`ِ کلیک‌شدنی: با صفحه‌کلید هم باز می‌شود و
 * `aria-expanded` وضعیتش را می‌گوید.
 */
function PanelBlock({ panel }: { panel: Panel }) {
  const [open, setOpen] = useState(false);
  const count = panel.rows.length;

  return (
    <div className={`ppanel${open ? ' on' : ''}`}>
      <button type="button" className="ppanel-head" aria-expanded={open}
        onClick={() => setOpen((v) => !v)}>
        <span className="ppanel-caret" aria-hidden="true">{open ? '▾' : '◂'}</span>
        <span className="ppanel-title">{panel.title}</span>
        {/* ⚠ شمارِ ردیف روی دکمه، نه داخلِ جدول: جدولی که بسته است و نگوید
            چقدر تویش هست، باز نمی‌شود. */}
        <span className="ppanel-count">{fmt(count)} ردیف</span>
      </button>

      {!open ? null : (
      <div className="ppanel-body">
      <p className="ppanel-note">{panel.note.replace(/\*\*/g, '')}</p>

      {panel.kind === 'repeat' ? (
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>سطل</th>
                <th className="num">
                  <Hint text="درخواست" help="چند بار کرالر خواست یک آدرس را بگیرد." />
                </th>
                <th className="num">
                  <Hint text="آدرسِ یکتا" help={'چند آدرسِ **متفاوت**. اگر این عدد '
                    + 'خیلی کوچک‌تر از ستونِ کناری است، یعنی همان چند آدرس بارها '
                    + 'تکرار شده‌اند و عددِ بزرگِ کناری بیشتر از آنچه هست به نظر '
                    + 'می‌رسد.'} />
                </th>
                <th className="num">
                  <Hint text="بار به ازای هر آدرس"
                    help={'یعنی هر آدرس در ۱۰ روز چند بار سرِ صف آمده. عددِ ۷ یعنی '
                      + 'تقریباً هر شب یک بار. برای سطل‌های «کشِ لینکِ مرده»، '
                      + '«robots.txt» و «منبعِ خاموش» این عدد جوابِ «فردا هم باز '
                      + 'سراغش می‌رویم؟» است — و جوابش بله است.'} />
                </th>
                <th className="num">
                  <Hint text="منبع" help="چند منبعِ متفاوت در این سطل سهم دارند." />
                </th>
              </tr>
            </thead>
            <tbody>
              {panel.rows.map((r) => (
                <tr key={r.bucket}>
                  <td>{BUCKET[r.bucket] ?? r.bucket}</td>
                  <td className="num">{fmt(r.requests)}</td>
                  <td className="num">{fmt(r.urls)}</td>
                  {/* ⚠ سه بار و بیشتر یعنی «هر شب دوباره» و باید دیده شود. */}
                  <td className={`num${(r.perUrl ?? 0) >= 3 ? ' bad' : ''}`}>
                    {r.perUrl != null ? `${r.perUrl}×` : '—'}
                  </td>
                  <td className="num">{fmt(r.sources)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {panel.kind === 'reasons' ? (
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr><th>دلیل</th><th className="num">تعداد</th><th className="num">سهم</th></tr>
            </thead>
            <tbody>
              {panel.rows.map((r) => (
                <tr key={r.key}>
                  <td>
                    <span className="hint" title={`${r.why}\n\nرشته‌ی خودِ کرالر: ${r.key}`}>
                      {r.label}
                    </span>
                  </td>
                  <td className="num">{fmt(r.n)}</td>
                  <td className="num dim">{r.share != null ? `${r.share}٪` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {panel.kind === 'notsent' ? (
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>رویداد</th><th>منبع</th><th>لینک در سایتِ منبع</th><th>دلیل</th>
              </tr>
            </thead>
            <tbody>
              {panel.rows.map((r, i) => (
                <tr key={`${r.url ?? 'x'}-${i}`}>
                  <td>{r.title}</td>
                  <td className="mono dim">{r.source ?? '—'}</td>
                  <td>
                    {r.url ? (
                      <a className="dup-link" href={r.url} target="_blank" rel="noreferrer"
                        title={r.url}>
                        {r.url}
                      </a>
                    ) : (
                      // نبودنِ لینک خودش خبر است و ردیف حذف نمی‌شود.
                      <span className="dim" title="این خوشه هیچ آدرسی ندارد — خودش یک ایراد است.">
                        —
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`chip pchip ${r.why === 'gone' ? 'hold' : ''}`}
                      title={NOTSENT_WHY[r.why]?.why ?? ''}>
                      {r.whyLabel}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      </div>
      )}
    </div>
  );
}

/**
 * جدولِ نمونه‌های یک مرحله — سه ردیفِ **واقعی** برای هر دسته.
 *
 * ═══ چرا این جدول مهم‌تر از آن است که به نظر می‌رسد ═══
 *
 * مرحله‌ی «رساندن به اپ» از قبل چنین جدولی داشت و همان یکی کارِ کلِ صفحه را
 * عوض کرد: عددِ «۱۹۸ تا واقعاً نرفته» می‌گوید چیزی خراب است، ولی سه ردیف با
 * لینکِ سایتِ منبع می‌گوید **چه چیزی**. بقیه‌ی دوازده مرحله بی آن، هر عددِ
 * ریزششان یک بن‌بست بود.
 *
 * ⚠ **تنبل است.** کوئری فقط وقتی می‌رود که آدم همان جدول را باز کند، و
 * react-query جوابش را نگه می‌دارد. دلیلش سرِ مایگریشنِ ۰۱۴۵ است: این یکی
 * از کش نمی‌خواند و جدول‌های خام را زنده می‌خواند، پس هر صدا زدنش یکی دو
 * ثانیه است. باز بودنِ پیش‌فرضِ سیزده‌تا یعنی سیزده کوئریِ سنگین برای
 * صفحه‌ای که آدم فقط می‌خواست از بالا به پایین بخواندش.
 *
 * ⚠ برچسبِ هر دسته از `fateMeta` می‌آید و نه از خودِ جواب: متنِ یک مفهوم
 * نباید دو نسخه داشته باشد، یکی در تایپ‌اسکریپت و یکی در SQL.
 */
function SamplePanel({ stage, city, fates }: {
  stage: string; city: string; fates: Fate[];
}) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['stage-samples', stage, city],
    queryFn: () => opsRepo.crawlerStageSamples(stage, city || null, 3),
    // ⚠ فقط وقتی باز شد. بدونِ این، `useQuery` همان اولِ رندر می‌دود و کلِ
    // نکته‌ی بسته بودن از بین می‌رود.
    enabled: open,
  });

  const buckets = q.data?.buckets ?? [];
  const total = buckets.reduce((s, b) => s + b.rows.length, 0);

  return (
    <div className={`ppanel${open ? ' on' : ''}`}>
      <button type="button" className="ppanel-head" aria-expanded={open}
        onClick={() => setOpen((v) => !v)}>
        <span className="ppanel-caret" aria-hidden="true">{open ? '▾' : '◂'}</span>
        <span className="ppanel-title">نمونه‌های واقعی</span>
        <span className="ppanel-count">
          {!open ? 'سه نمونه از هر دسته' : q.isLoading ? '…' : `${fmt(total)} ردیف`}
        </span>
      </button>

      {!open ? null : (
        <div className="ppanel-body">
          {q.isLoading ? <p className="dim">در حالِ گرفتن…</p> : null}
          {q.error ? <p className="err mono">{errText(q.error)}</p> : null}
          {q.data?.error ? (
            <p className="dim">این دیتابیس اسکیمای کرالر را ندارد.</p>
          ) : null}
          {q.data && !q.data.error && !buckets.length ? (
            <p className="dim">
              در پنجره‌ی {q.data.days} روزه نمونه‌ای برای این مرحله نیست.
            </p>
          ) : null}

          {buckets.map((b) => {
            const meta = fateMeta(stage, b.bucket)
              ?? fates.find((f) => f.id === b.bucket) ?? null;
            return (
              <div key={b.bucket} className="psample">
                <div className="psample-head">
                  <span className={`chip pchip ${meta?.kind ?? ''}`}
                    title={meta?.why?.replace(/\*\*/g, '')
                      ?? 'این دسته را این صفحه نمی‌شناسد؛ شناسه‌اش همان است که دیتابیس داد.'}>
                    {meta?.label ?? b.bucket}
                  </span>
                </div>
                <div className="scrollx">
                  <table className="tbl">
                    <thead>
                      <tr><th>چیست</th><th>لینک</th><th>یادداشت</th><th>کِی</th></tr>
                    </thead>
                    <tbody>
                      {b.rows.map((r, i) => (
                        <tr key={`${b.bucket}-${i}`}>
                          <td>{r.title || '—'}</td>
                          <td>
                            {r.url ? (
                              <a className="dup-link" href={r.url}
                                target="_blank" rel="noreferrer" title={r.url}>
                                {r.url}
                              </a>
                            ) : (
                              // نبودنِ لینک خودش خبر است و ردیف حذف نمی‌شود.
                              <span className="dim" title="این ردیف هیچ آدرسی ندارد.">—</span>
                            )}
                          </td>
                          <td className="dim">{r.note || '—'}</td>
                          <td className="dim">{ago(r.at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          {buckets.length ? (
            <p className="ppanel-note">
              دسته‌ای که این‌جا نیست، در پنجره‌ی {q.data?.days} روزه نمونه‌ای
              نداشت. ⚠ «هنوز استخراج نشده» سه‌تا از **هر جای** صف می‌آورد و نه
              تازه‌ترین‌ها — آن صف صدها هزار ردیف دارد و مرتب کردنش گران است.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** کارنامه‌ی خودِ فاز — چند بار دوید، چند بارش شکست، چقدر طول کشید، و چرا شکست. */
function Health({ row }: { row: StageRow }) {
  const h = row.health;
  if (!h) {
    return (
      <p className="phealth dim">
        <Hint text="این فاز ردیفی در جدولِ اجراها ندارد"
          help={'یعنی `crawler.runs` هیچ اجرایی به این نام ثبت نکرده. دو حالت '
            + 'دارد و از این‌جا نمی‌شود فرقشان را گفت: یا این مرحله بخشی از '
            + 'فازِ دیگری است و جدا ثبت نمی‌شود، یا اصلاً نچرخیده. عددهای '
            + 'بالا باز هم درست‌اند — آن‌ها از جدولِ خودِ آیتم‌ها می‌آیند، نه '
            + 'از جدولِ اجراها.'} />
        {' '}— عددهای بالا از خودِ آیتم‌ها می‌آیند و سرِ جایشان‌اند.
      </p>
    );
  }
  const fam = ERROR_FAMILY[h.errorFamily];
  return (
    <div className="phealth">
      <span>
        <Hint text="اجرا" help={'چند بار این فاز دویده. ⚠ واحدش «یک بار دویدنِ '
          + 'فاز» است و نه «یک آیتم» — فازِ گرفتنِ صفحه یک بار روی یک منبع '
          + 'می‌دود و در همان یک بار صدها آدرس می‌گیرد. برای همین این عدد '
          + 'ده‌ها برابر کوچک‌تر از عددهای بالاست.'} />
        {': '}<Two v={h.runs} />
      </span>
      <span>
        <Hint text="شکست" help={'اجرایی که با خطا تمام شد. ⚠ با «ریزشِ آیتم» '
          + 'یکی نیست: ریزش یعنی خودِ کار درست انجام شد و آیتم رد شد؛ شکست '
          + 'یعنی خودِ مرحله وسطِ کار خراب شد و معلوم نیست چند آیتم اصلاً '
          + 'دیده نشده‌اند.'} />
        {': '}<Two v={h.failures} bad />
        {h.failRate != null ? <span className="dim"> ({h.failRate}٪)</span> : null}
      </span>
      <span>
        <Hint text="میانه" help={'نصفِ اجراها از این سریع‌تر بوده‌اند و نصفِ '
          + 'دیگر کندتر. میانگین عمداً نیامده: یک اجرای پنج‌دقیقه‌ای در میانِ '
          + 'پنجاه اجرای ده‌ثانیه‌ای میانگین را بی‌معنی می‌کند.'} />
        {`: ${secs(h.p50)}`}
      </span>
      <span className={h.slow ? 'err' : undefined}>
        <Hint text="صدکِ ۹۵" help={'کندترین ۵٪ِ اجراها از این کندترند. '
          + 'وقتی این عدد چند برابرِ میانه است یعنی بیشترِ اجراها سریع‌اند '
          + 'ولی یک دنباله‌ی بلند هست — معمولاً یکی دو منبعِ کند که کلِ '
          + 'زنجیره را عقب می‌اندازند. مثلِ صفِ نانوایی که همه دو دقیقه‌اند '
          + 'و یک نفر بیست دقیقه.'} />
        {`: ${secs(h.p95)}`}
      </span>
      <span className="dim">آخرین اجرا: {ago(h.lastRun)}</span>
      {h.lastError ? (
        <span className="pfam" title={`${fam.why}\n\nمتنِ کاملِ آخرین خطا:\n${h.lastError}`}>
          جنسِ آخرین خطا: <b className="err">{fam.label}</b>
        </span>
      ) : (
        <span className="dim">خطایی ثبت نشده</span>
      )}
    </div>
  );
}

function StageCard({ row, index, city }: { row: StageRow; index: number; city: string }) {
  const input = row.input!.v;
  const aside = row.fates.filter((f) => f.aside);
  const main = row.fates.filter((f) => !f.aside);

  return (
    <li className={`pstage${row.idle ? ' idle' : ''}`}>
      <div className="pstage-head">
        <span className="pstage-no">{index + 1}</span>
        <b className="pstage-title">{row.title}</b>
        <span className="mono dim" title="نامِ این فاز در جدولِ اجراهای کرالر">
          {row.stage}
        </span>
        <span className="pstage-unit">
          <Hint text={`واحد: ${row.unit}`}
            help={'هر عددِ این مرحله چه چیزی را می‌شمارد. ⚠ واحدِ مرحله‌ها با '
              + 'هم فرق می‌کند و همین بزرگ‌ترین منبعِ سوءتفاهم است: «۴٬۵۷۴ '
              + 'سند» و «۲۹٬۹۹۳ کاندیدا» تناقض نیستند، دو چیزِ متفاوت را '
              + 'می‌شمارند.'} />
        </span>
        {row.idle ? (
          <span className="pstage-idle"
            title="در این پنجره هیچ اجرایی از این فاز ثبت نشده.">نچرخیده</span>
        ) : null}
      </div>

      <p className="pstage-what">{row.what}</p>
      <p className="pstage-like">
        <span className="dim">مثلِ دنیای واقعی — </span>{row.like}
      </p>

      <div className="pstage-io">
        <span>
          <span className="dim">ورودی</span> <Two v={input} />{' '}
          <span className="dim">{row.input!.label}</span>
        </span>
        <span className="pstage-arrow" aria-hidden="true">⟵</span>
        <span>
          <span className="dim">خروجی</span> <Two v={row.output!.v} />{' '}
          <span className="dim">{row.output!.label}</span>
        </span>
      </div>

      {row.unitShift ? (
        <p className="note pstage-shift">
          <b>⚠ واحد عوض می‌شود.</b> {row.unitShift}
        </p>
      ) : null}

      {/* مرحله‌ای که ورودی‌اش را تقسیم نمی‌کند نوار ندارد — نوارِ تک‌رنگ
          چیزی نمی‌گوید و فقط شبیهِ نوارِ بقیه به نظر می‌رسد. */}
      {main.length ? <FateBar fates={main} total={input.d10} unit={row.unit} /> : null}

      <div className="chiprow pstage-fates">
        {main.map((f) => (
          <span key={f.id} className={`chip pchip ${f.kind}`} title={fateHelp(f, row.unit)}>
            {f.label} <b>{fmt(f.v.d10)}</b>
            {f.share != null ? <span className="dim"> · {f.share}٪</span> : null}
          </span>
        ))}
        {/* ⚠ باقی‌مانده بی‌صدا گم نمی‌شود. جمعی که با ورودی نخواند یعنی یک
            دسته‌ی تازه در دیتابیس هست که این گزارش اسمی برایش ندارد، و آن
            دقیقاً چیزی است که باید دیده شود. */}
        {row.residual > 0 ? (
          <span className="chip pchip warn"
            title={'این تعداد از ورودیِ مرحله در هیچ‌کدام از دسته‌های بالا '
              + 'نیفتاد. یعنی دیتابیس دسته‌ای دارد که این گزارش نمی‌شناسدش. '
              + 'عمداً نشان داده می‌شود: جمعی که سر به سر نشود باید دیده '
              + 'شود، نه اینکه گرد شود.'}>
            حساب‌نشده <b>{fmt(row.residual)}</b>
          </span>
        ) : null}
      </div>

      {aside.length ? (
        <div className="chiprow pstage-fates">
          <span className="dim" style={{ fontSize: 12 }}>
            <Hint text="شمارشِ جداگانه:"
              help={'این عددها ورودیِ این مرحله را تقسیم نمی‌کنند — از جدولِ '
                + 'دیگری می‌آیند و واحدشان با ورودی یکی نیست. برای همین در '
                + 'نوارِ رنگی و در درصدها نیامده‌اند، ولی دانستنشان لازم است.'} />
          </span>
          {aside.map((f) => (
            <span key={f.id} className={`chip pchip ${f.kind}`} title={fateHelp(f, row.unit)}>
              {f.label} <b>{fmt(f.v.d10)}</b>
            </span>
          ))}
        </div>
      ) : null}

      {row.panels.map((p) => <PanelBlock key={p.kind + p.title} panel={p} />)}

      {/* ⚠ شناسه‌ی مرحله و نه نامِ فاز: `row.stage` نامی است که در
          `crawler.runs` پیدا شد (`vnext_detect`) و تابعِ نمونه‌ها با شناسه‌ی
          خودِ نمودار کار می‌کند (`detect`). */}
      <SamplePanel stage={row.id} city={city} fates={row.fates} />

      <Health row={row} />
    </li>
  );
}

export function StageFlow({ report, city }: { report: StageReport; city: string }) {
  return (
    <>
      <p className="dim" style={{ marginBottom: 6 }}>
        این نمودار از دیدِ <b>مرحله‌ها</b>ست: از بالا به پایین بخوانید. هر
        مرحله می‌گوید چه کار می‌کند، چقدر کار به آن رسیده، چقدرش رد شده، چقدرش
        ریخته، و خودِ مرحله چند بار شکسته و چرا.
      </p>
      {/* استعاره‌ی واحد برای هر ده مرحله. استعاره‌ی پراکنده از بی‌استعاره
          بدتر است — آدم باید هر بار از نو خودش را جا بیندازد. */}
      <p className="note">
        <b>کلِ کار در یک جمله:</b> کسی را فرستاده‌ایم در شهر بگردد و اعلانِ
        رویدادها را جمع کند. اول <b>می‌پرسد تابلوهای اعلانات کجایند</b>، سرِ
        هر کدام می‌رود و نگاه می‌کند به درد می‌خورد یا نه، و آن‌که می‌خورد را
        در دفترچه‌ی مسیرِ روزانه‌اش می‌نویسد. از آن به بعد هر شب از تابلوها
        عکس می‌گیرد، کاغذهای روی هر تابلو را می‌شمارد، هر کاغذ را می‌خواند،
        کاغذهای مربوط به یک برنامه را در یک پوشه می‌گذارد، و آخرش تصمیم
        می‌گیرد کدام پوشه روی تابلوی خودمان برود. سیزده مرحله‌ی زیر همین‌اند.
      </p>
      <p className="note legend">
        هر عدد دو تاست: اولی <b>۱۰ روزِ اخیر</b> و دومیِ کم‌رنگ{' '}
        <b>آخرین اجرا</b>. نوارِ رنگیِ زیرِ هر مرحله <b>ورودیِ همان مرحله</b> را
        تقسیم می‌کند (نه کلِ خطِ لوله را)، پس همیشه پر است. رنگ‌ها:{' '}
        {(['pass', 'drop', 'hold', 'info'] as FateKind[]).map((k) => (
          <span key={k} className={`plegend ${k}`} title={KIND[k].help}>
            <i /> {KIND[k].label}
          </span>
        ))}
        . موس را روی هر برچسب، هر چیپ و هر تکه‌ی نوار نگه دارید تا معنی‌اش
        با یک مثال بیاید.
      </p>

      <ol className="pflow">
        {report.rows.map((r, i) => (
          <StageCard key={r.id} row={r} index={i} city={city} />
        ))}
      </ol>

      {/* ⚠ فازی که این گزارش نمی‌شناسدش دور ریخته نمی‌شود. اگر کرالر فازِ
          تازه‌ای اضافه کند، چیزی که آدم باید ببیند دقیقاً همین است — نه
          صفحه‌ای که انگار آن فاز وجود ندارد. */}
      {report.unknown.length ? (
        <p className="note">
          <b>فازهایی که این نمودار نمی‌شناسدشان:</b>{' '}
          {report.unknown.map((s) => (
            <span key={s.stage} className="mono"
              title={`${fmt(s.runs?.d10)} اجرا در ۱۰ روز · ${fmt(s.failures?.d10)} شکست`}>
              {s.stage}{' '}
            </span>
          ))}
          — یعنی کرالر فازی دارد که در فهرستِ ده‌تاییِ بالا نیست. اعدادشان در
          جدولِ «فازها»ی پایینِ صفحه هست؛ اگر ماندگار شد، باید به این نمودار
          هم اضافه شوند.
        </p>
      ) : null}
    </>
  );
}
